import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { isChangeManager } from '../changePermissions';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  DEFAULT_APPROVAL_SETTINGS,
  DEFAULT_PLAN_EDITOR_ROLES,
  PLAN_EDITOR_ROLE_OPTIONS,
  normalizePlanEditorRoles,
  type ApprovalSettings,
} from '../changeApprovalPlan';
import { FORM_EDITABLE_CONFIGURABLE_STATUSES, labelChangeStatus } from '../changeLabels';

const ChangeApprovalSettingsPanel: React.FC = () => {
  const { user } = useAuth();
  const canManage = isChangeManager(user);
  const [settings, setSettings] = useState<ApprovalSettings>(DEFAULT_APPROVAL_SETTINGS);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { showAlert } = useModal();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await changesApi.getApprovalSettings();
      setSettings(next);
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao carregar os grupos de aprovação.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    if (
      !settings.enable_riscos_impactos &&
      !settings.enable_camud_go_no_go &&
      settings.enable_gestao_mudancas === false
    ) {
      setError('Mantenha ao menos um grupo de aprovação habilitado.');
      return;
    }
    setSaving(true);
    try {
      const saved = await changesApi.replaceApprovalSettings({
        enable_riscos_impactos: settings.enable_riscos_impactos,
        enable_camud_go_no_go: settings.enable_camud_go_no_go,
        enable_gestao_mudancas: settings.enable_gestao_mudancas !== false,
        creator_approver_roles: settings.creator_approver_roles || ['criador', 'gestor'],
        plan_editor_roles: normalizePlanEditorRoles(settings.plan_editor_roles),
        approval_order_mode: settings.approval_order_mode || 'livre',
        form_editable_statuses:
          settings.form_editable_statuses?.length
            ? settings.form_editable_statuses
            : ['avaliar', 'agendado'],
      });
      setSettings(saved);
      setError(null);
      await showAlert({
        title: 'Aprovações de comitê',
        message: 'As configurações de aprovação de comitê foram salvas.',
        variant: 'success',
      });
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar os grupos de aprovação.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-muted mb-0">Carregando grupos de aprovação...</p>;
  }

  return (
    <div>
      <p className="text-muted">
        Quais <strong>trilhas do comitê</strong> existem no status Autorizar (por mudança, no bloco
        Quem aprova esta mudança). O padrão é CAMUD Go/No-Go e Gestor de mudanças. Cadastre áreas
        (CINFRA, Contratação…) em <strong>Estrutura → Grupos</strong>; aqui só liga ou desliga cada
        trilha. <strong>Emergencial</strong> não usa este comitê — usa fluxo em Aprovação emergencial.
      </p>
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      <Form.Check
        type="switch"
        id="approval-settings-camud"
        className="mb-2"
        label="CAMUD Go/No-Go"
        checked={settings.enable_camud_go_no_go}
        disabled={!canManage}
        onChange={(e) =>
          setSettings((prev) => ({ ...prev, enable_camud_go_no_go: e.target.checked }))
        }
      />
      <Form.Check
        type="switch"
        id="approval-settings-riscos"
        className="mb-2"
        label="Riscos e impactos"
        checked={settings.enable_riscos_impactos}
        disabled={!canManage}
        onChange={(e) =>
          setSettings((prev) => ({ ...prev, enable_riscos_impactos: e.target.checked }))
        }
      />
      <Form.Check
        type="switch"
        id="approval-settings-gestao"
        className="mb-3"
        label="Gestor de mudanças"
        checked={settings.enable_gestao_mudancas !== false}
        disabled={!canManage}
        onChange={(e) =>
          setSettings((prev) => ({ ...prev, enable_gestao_mudancas: e.target.checked }))
        }
      />
      <p className="small text-muted mb-3 ms-4">
        Ligado: card automático na aba Aprovações para Admin / flag Gestor de mudanças.
      </p>
      <hr className="my-3" />
      <p className="mb-2">
        <strong>Quem monta o plano de aprovação</strong>
      </p>
      <p className="text-muted small">
        Quem pode ver e montar o bloco <strong>Quem aprova esta mudança</strong> na ficha da GMUD.
        Quem não estiver marcado não vê esse bloco. O padrão é Administrador, Gestor de mudanças e
        o criador. Marque também dono ou solicitante se precisarem montar o comitê. Mantenha ao menos
        uma opção.
      </p>
      <div className="mb-3 d-flex flex-column gap-2">
        {PLAN_EDITOR_ROLE_OPTIONS.map((item) => {
          const selected = normalizePlanEditorRoles(settings.plan_editor_roles);
          const checked = selected.includes(item.value);
          return (
            <Form.Check
              key={item.value}
              type="checkbox"
              id={`plan-editor-${item.value}`}
              label={item.label}
              checked={checked}
              disabled={!canManage}
              onChange={(e) => {
                setSettings((prev) => {
                  const current = normalizePlanEditorRoles(prev.plan_editor_roles);
                  const next = e.target.checked
                    ? [...current, item.value]
                    : current.filter((role) => role !== item.value);
                  return {
                    ...prev,
                    plan_editor_roles: next.length ? next : [...DEFAULT_PLAN_EDITOR_ROLES],
                  };
                });
              }}
            />
          );
        })}
      </div>
      <hr className="my-3" />
      <p className="mb-2">
        <strong>Edição dos dados da mudança</strong>
      </p>
      <p className="text-muted small">
        Marque em quais status o criador, solicitante e demais vínculos podem usar &quot;Editar
        dados&quot; na mudança. Gestor de mudanças sempre pode editar quando o status estiver
        marcado. Encerrada e cancelada nunca entram aqui.
      </p>
      <div className="mb-3 d-flex flex-wrap gap-3">
        {FORM_EDITABLE_CONFIGURABLE_STATUSES.map((status) => {
          const checked = (settings.form_editable_statuses || ['avaliar', 'agendado']).includes(
            status,
          );
          return (
            <Form.Check
              key={status}
              type="checkbox"
              id={`form-editable-${status}`}
              label={labelChangeStatus(status)}
              checked={checked}
              disabled={!canManage}
              onChange={(e) => {
                setSettings((prev) => {
                  const current = prev.form_editable_statuses || ['avaliar', 'agendado'];
                  const next = e.target.checked
                    ? [...current, status]
                    : current.filter((item) => item !== status);
                  return {
                    ...prev,
                    form_editable_statuses: next.length ? next : ['avaliar'],
                  };
                });
              }}
            />
          );
        })}
      </div>
      {canManage ? (
        <Button size="sm" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar aprovações de comitê'}
        </Button>
      ) : (
        <p className="text-muted small mb-0">Somente administrador ou analista altera este cadastro.</p>
      )}
    </div>
  );
};

export default ChangeApprovalSettingsPanel;
